import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import legacyTheme from '../../theme';
import { NextThemeProvider, tokens } from '../../theme-next';
import FirebaseUploadComponent from '../CreateCotation/FirebaseUploadComponent';

interface DocumentRequest {
  id: number;
  docType: string;
  label: string;
  reason: string | null;
  status: 'open' | 'fulfilled' | 'cancelled';
  fulfilledUrl: string | null;
  fulfilledAt: string | null;
  createdAt: string;
}

interface DocumentRequestsResponse {
  success: boolean;
  data?: DocumentRequest[];
  error?: string;
}

interface FulfillResponse {
  success: boolean;
  remainingOpen?: number;
  newStatus?: string | null;
  error?: string;
}

const ResubmissionInner = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const regIdNum = Number(registrationId);
  const validId = Number.isInteger(regIdNum) && regIdNum > 0;

  const { data, isLoading, isError, refetch } = useQuery<DocumentRequestsResponse>({
    queryKey: ['cgDocumentRequests', regIdNum],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<DocumentRequestsResponse>(
        'get',
        `/api/cg/${regIdNum}/document-requests`,
      );
      return response.data;
    },
    enabled: validId,
  });

  const requests = data?.data ?? [];
  const openRequests = requests.filter((r) => r.status === 'open');
  const fulfilledCount = requests.filter((r) => r.status === 'fulfilled').length;

  if (!validId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Identifiant de CG invalide.</Alert>
      </Box>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Chargement…</Typography>
      </Box>
    );
  }
  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Impossible de charger les pièces demandées.</Alert>
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Aucune pièce complémentaire demandée pour cette CG.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: tokens.color.bg, minHeight: '100vh', padding: '20px 24px 28px' }}>
      <Box sx={{ maxWidth: 920, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: tokens.color.yellow,
            }}
          >
            Pièces complémentaires · Dossier #{regIdNum}
          </Typography>
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 900,
              color: tokens.color.dark,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              marginTop: '6px',
            }}
          >
            Redéposez les pièces demandées
          </Typography>
          <Typography
            sx={{ fontSize: '14px', color: tokens.color.dark, marginTop: '8px', maxWidth: 660, lineHeight: 1.55 }}
          >
            Skipcar a besoin des pièces ci-dessous pour finaliser votre demande. Quand toutes seront déposées,
            le dossier repartira automatiquement en review.
          </Typography>
        </Box>

        <Box>
          <LinearProgress
            variant="determinate"
            value={(fulfilledCount / requests.length) * 100}
            sx={{ height: 6, borderRadius: 3, backgroundColor: tokens.color.border }}
          />
          <Typography sx={{ fontSize: '12px', color: tokens.color.textMuted, marginTop: '6px' }}>
            {fulfilledCount} / {requests.length} pièce{requests.length > 1 ? 's' : ''} déposée
            {fulfilledCount > 1 ? 's' : ''}.
          </Typography>
        </Box>

        <Stack spacing={2}>
          {requests.map((req) => (
            <RequestRow
              key={req.id}
              request={req}
              onFulfilled={async () => {
                await refetch();
                await queryClient.invalidateQueries({ queryKey: ['useCGList'] });
              }}
            />
          ))}
        </Stack>

        {openRequests.length === 0 && (
          <Alert severity="success" icon={<DoneAllIcon />}>
            Toutes les pièces ont été redéposées. Le dossier est de nouveau en review chez Skipcar.
          </Alert>
        )}

        <ThemeProvider theme={legacyTheme}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button className="secondaryDealActionButton" onClick={() => navigate('/list-cg')}>
              ← Retour à la liste
            </Button>
          </Stack>
        </ThemeProvider>
      </Box>
    </Box>
  );
};

const RequestRow = ({
  request,
  onFulfilled,
}: {
  request: DocumentRequest;
  onFulfilled: () => Promise<void>;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FirebaseUploadComponent gère lui-même upload + preview + retry. On
  // intercepte juste la complétion pour rattacher l'URL à la request.
  const handleUploadComplete = useCallback(
    async (url: string) => {
      if (request.status !== 'open') return;
      setSubmitting(true);
      setError(null);
      try {
        const response = await makeAuthenticatedApiCall<FulfillResponse>(
          'post',
          `/api/cg/document-requests/${request.id}/fulfill`,
          { fulfilledUrl: url },
        );
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Erreur fulfill');
        }
        await onFulfilled();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Erreur lors du rattachement");
      } finally {
        setSubmitting(false);
      }
    },
    [request.id, request.status, onFulfilled],
  );

  const isFulfilled = request.status === 'fulfilled';

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `1px solid ${isFulfilled ? tokens.color.green : tokens.color.border}`,
        borderRadius: `${tokens.radius.xl}px`,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 800, color: tokens.color.dark }}>
            {request.label}
          </Typography>
          {request.reason && (
            <Typography sx={{ fontSize: '13px', color: tokens.color.textMuted, marginTop: '2px' }}>
              {request.reason}
            </Typography>
          )}
        </Box>
        {isFulfilled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: tokens.color.green }}>
            <DoneAllIcon fontSize="small" />
            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Déposé</Typography>
          </Box>
        )}
        {submitting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={16} thickness={5} />
            <Typography sx={{ fontSize: '13px' }}>Rattachement…</Typography>
          </Box>
        )}
      </Stack>

      {!isFulfilled && (
        <FirebaseUploadComponent
          source={`cg-doc-requests/${request.id}`}
          onUploadComplete={(url) => void handleUploadComplete(url)}
          uploadedUrls={request.fulfilledUrl ? [request.fulfilledUrl] : []}
        />
      )}

      {error && (
        <Typography sx={{ fontSize: '12px', color: tokens.color.red, fontWeight: 600 }}>{error}</Typography>
      )}
    </Box>
  );
};

const CGResubmissionPage = () => (
  <NextThemeProvider>
    <ResubmissionInner />
  </NextThemeProvider>
);

export default CGResubmissionPage;
